/**
 * Operator dashboard — admin-styled tab layout, scoped to this storefront.
 *
 * Tabs (admin can toggle visibility via Settings → Role Access):
 *   overview     - Daily tiles + Weekly settlement card
 *   players      - Roster, search, activity drill, load / cashout
 *   cashouts     - Pending cashout requests (operator approves)
 *   lottery      - Place Ticket + Recent Drawings + Tickets (with scan viewer)
 *   transactions - Every txn for this location
 *   reports      - Scoped financial report + weekly settlement
 *   profit       - Daily + weekly profit, per-cashier owed (Mark Paid)
 *   cashiers     - Manage storefront staff
 *   settings     - Location feature flags (self-serve toggles)
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import RoleDashboardLayout, { useDashboardPermissions } from '../components/RoleDashboardLayout';
import DailyReportTiles from '../components/DailyReportTiles';
import LoadsByMethodCard from '../components/LoadsByMethodCard';
import CloseShiftButton from '../components/CloseShiftButton';
import OperatorWeeklyCard from '../components/OperatorWeeklyCard';
import PendingCashoutsPanel from '../components/PendingCashoutsPanel';
import AllCashoutsPanel from '../components/AllCashoutsPanel';
import { PlayerDrilldownProvider } from '../components/PlayerDrilldownContext';
import TransactionsTab from '../components/TransactionsTab';
import PlayersTab from '../components/PlayersTab';
import CashiersPanel from '../components/CashiersPanel';
import OperatorSettingsTab from '../components/OperatorSettingsTab';
import OperatorProfitTab from '../components/OperatorProfitTab';
import OperatorLotteryTab from '../components/OperatorLotteryTab';
import { WheelsAdminPanel } from '../pages/admin/WheelsAdminPanel';
import ScopedFinancialReport from '../components/ScopedFinancialReport';
import {
    BarChart3, Users, Inbox, Activity, FileText, UserCog, Settings as SettingsIcon, Ticket, DollarSign, Sparkles,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const OperatorDashboard = () => {
    const { logout } = useAuth();
    const allowed = useDashboardPermissions();
    const [stats, setStats] = useState(null);
    const [me, setMe] = useState(null);

    useEffect(() => {
        axios.get(`${API}/operator/stats`, { withCredentials: true }).then(({ data }) => setStats(data)).catch(() => { });
        axios.get(`${API}/cashier/me`, { withCredentials: true }).then(({ data }) => setMe(data)).catch(() => { });
    }, []);

    const tabs = [
        { value: 'overview',     label: 'Overview',     icon: BarChart3,    allowedKey: 'overview' },
        { value: 'players',      label: 'Players',      icon: Users,        allowedKey: 'players' },
        { value: 'cashouts',     label: 'Cashouts',     icon: Inbox,        allowedKey: 'cashouts' },
        { value: 'lottery',      label: 'Lottery',      icon: Ticket,       allowedKey: 'lottery' },
        { value: 'wheels',       label: 'Wheels',       icon: Sparkles,     allowedKey: 'wheels' },
        { value: 'transactions', label: 'Transactions', icon: Activity,     allowedKey: 'transactions' },
        { value: 'reports',      label: 'Reports',      icon: FileText,     allowedKey: 'reports' },
        { value: 'profit',       label: 'Profit',       icon: DollarSign,   allowedKey: 'profit' },
        { value: 'cashiers',     label: 'Cashiers',     icon: UserCog,      allowedKey: 'cashiers' },
        { value: 'settings',     label: 'Settings',     icon: SettingsIcon, allowedKey: 'settings' },
    ];

    return (
        <PlayerDrilldownProvider>
        <RoleDashboardLayout
            title={stats?.name || me?.operator?.name || 'Storefront'}
            subtitle="Operator console · scoped to this location"
            rolePill="OPERATOR"
            tabs={tabs}
            allowed={allowed}
            onLogout={logout}
            children={{
                overview: (
                    <div className="space-y-5">
                        <DailyReportTiles />
                        <LoadsByMethodCard />
                        <CloseShiftButton />
                        <OperatorWeeklyCard />
                    </div>
                ),
                players: <PlayersTab showCreateButton={true} canCashout={true} />,
                cashouts: (
                    <div className="space-y-6">
                        <PendingCashoutsPanel role="operator" />
                        <AllCashoutsPanel role="cashier" />
                    </div>
                ),
                lottery: <OperatorLotteryTab />,
                wheels: <WheelsAdminPanel role="operator" />,
                transactions: <TransactionsTab endpoint="/operator/transactions" />,
                reports: (
                    <div className="space-y-6">
                        <ScopedFinancialReport endpoint="/operator/reports/financial" />
                        <OperatorWeeklyCard />
                    </div>
                ),
                profit: <OperatorProfitTab />,
                cashiers: <CashiersPanel />,
                settings: <OperatorSettingsTab />,
            }}
        />
        </PlayerDrilldownProvider>
    );
};

export default OperatorDashboard;
